/**
 * 웹 오디오 API를 사용한 효과음 생성
 */

class SoundEffects {
  private audioContext: AudioContext | null = null;

  constructor() {
    // 브라우저 호환성 체크
    if (typeof window !== 'undefined') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  /**
   * 카운트다운 틱톡 소리
   */
  playCountdown() {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.value = 800; // 높은 틱 소리
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.1);
  }

  /**
   * 순차 발표 두둥 소리
   */
  playReveal() {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.value = 523.25; // C5 음
    oscillator.type = 'triangle';

    gainNode.gain.setValueAtTime(0.2, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.3);
  }

  /**
   * 당첨 축하 소리 (상승 멜로디)
   */
  playWin() {
    if (!this.audioContext) return;

    const notes = [523.25, 659.25, 783.99, 1046.50]; // C-E-G-C (도미솔도)
    const startTime = this.audioContext.currentTime;

    notes.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.value = freq;
      oscillator.type = 'sine';

      const time = startTime + index * 0.15;
      gainNode.gain.setValueAtTime(0.3, time);
      gainNode.gain.exponentialRampToValueAtTime(0.01, time + 0.15);

      oscillator.start(time);
      oscillator.stop(time + 0.15);
    });
  }

  /**
   * 돈까스 바삭 소리 (화이트 노이즈 + 크런치)
   */
  playDonkatsu() {
    if (!this.audioContext) return;

    // 바삭한 소리 (화이트 노이즈)
    const bufferSize = this.audioContext.sampleRate * 0.5; // 0.5초
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    // 화이트 노이즈 생성
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.audioContext.createBufferSource();
    noise.buffer = buffer;

    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000; // 바삭한 주파수

    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0.15, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

    noise.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    noise.start(this.audioContext.currentTime);
    noise.stop(this.audioContext.currentTime + 0.5);

    // 추가 펑 소리
    setTimeout(() => {
      const oscillator = this.audioContext!.createOscillator();
      const gain = this.audioContext!.createGain();

      oscillator.connect(gain);
      gain.connect(this.audioContext!.destination);

      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';

      gain.gain.setValueAtTime(0.2, this.audioContext!.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + 0.2);

      oscillator.start(this.audioContext!.currentTime);
      oscillator.stop(this.audioContext!.currentTime + 0.2);
    }, 100);
  }

  /**
   * 탈락 아쉬운 소리 (하강 멜로디)
   */
  playLose() {
    if (!this.audioContext) return;

    const notes = [523.25, 392.00, 329.63]; // C-G-E (도솔미 하강)
    const startTime = this.audioContext.currentTime;

    notes.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      oscillator.frequency.value = freq;
      oscillator.type = 'sine';

      const time = startTime + index * 0.2;
      gainNode.gain.setValueAtTime(0.2, time);
      gainNode.gain.exponentialRampToValueAtTime(0.01, time + 0.2);

      oscillator.start(time);
      oscillator.stop(time + 0.2);
    });
  }

  /**
   * 함성 박수 소리 (군중 응원)
   */
  playApplause() {
    if (!this.audioContext) return;

    // 박수 소리 (화이트 노이즈 버스트)
    const duration = 3.0; // 3초간 박수
    const bufferSize = this.audioContext.sampleRate * duration;
    const buffer = this.audioContext.createBuffer(2, bufferSize, this.audioContext.sampleRate);
    
    // 스테레오로 더 풍성하게
    for (let channel = 0; channel < 2; channel++) {
      const data = buffer.getChannelData(channel);
      
      for (let i = 0; i < bufferSize; i++) {
        // 박수 패턴 (더 빠르고 짧게)
        const t = i / this.audioContext.sampleRate;
        const envelope = Math.sin(t * Math.PI / duration); // 페이드 인/아웃
        const burst = Math.sin(t * 60 + Math.random() * 20) * 0.5 + 0.5; // 빠른 박수 리듬
        data[i] = (Math.random() * 2 - 1) * envelope * burst * 0.8; // 볼륨 조정
      }
    }

    const applause = this.audioContext.createBufferSource();
    applause.buffer = buffer;

    // 필터로 박수 소리 다듬기 (더 높은 주파수)
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2500; // 박수 주파수 (1500 → 2500)
    filter.Q.value = 1.0; // Q 값 증가 (더 선명하게)

    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0.01, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.5); // 더 크게
    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime + 2.5);
    gainNode.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 3.0);

    applause.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    applause.start(this.audioContext.currentTime);
    applause.stop(this.audioContext.currentTime + duration);

    // 함성 소리 (더 높은 주파수로 변경, 볼륨 줄임)
    const cheer = this.audioContext.createOscillator();
    const cheerGain = this.audioContext.createGain();
    
    cheer.connect(cheerGain);
    cheerGain.connect(this.audioContext.destination);
    
    // 300-400Hz로 변경 (150-200Hz → 뿌우웅 방지)
    cheer.frequency.setValueAtTime(300, this.audioContext.currentTime);
    cheer.frequency.linearRampToValueAtTime(400, this.audioContext.currentTime + 1.5);
    cheer.type = 'sine'; // sawtooth → sine (부드럽게)
    
    cheerGain.gain.setValueAtTime(0.01, this.audioContext.currentTime);
    cheerGain.gain.linearRampToValueAtTime(0.08, this.audioContext.currentTime + 0.5); // 0.15 → 0.08
    cheerGain.gain.setValueAtTime(0.08, this.audioContext.currentTime + 2.5);
    cheerGain.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 3.0);
    
    cheer.start(this.audioContext.currentTime);
    cheer.stop(this.audioContext.currentTime + 3.0);
  }
}

// 싱글톤 인스턴스 생성
export const soundEffects = new SoundEffects();
